import { Injectable } from '@angular/core';
import { combineLatest, Observable } from 'rxjs';
import { map, shareReplay } from 'rxjs/operators';
import { DsChipColorVariant, DsChipSize, DsHeaderBarChipConfig } from '@levelaccess/design-system';

import { FeatureFlagCollection } from '../../../shared/interfaces/feature-flag.interface';
import { FeatureFlagService } from './feature-flag/feature-flag.service';
import { NavService } from './navigation/nav.service';
import { getNavigationContextTranslationKey, INavigationContextItem } from './navigation/nav-structure';
import { TranslateService } from '../translate/translate.service';

@Injectable({
  providedIn: 'root',
})
export class StandardizedHeaderBarService {
  public readonly useStandardizedHeaderBar$: Observable<boolean>;
  private readonly wayfindingChipConfig$: Observable<DsHeaderBarChipConfig[]>;

  constructor(
    private readonly featureFlagService: FeatureFlagService,
    private readonly navService: NavService,
    private readonly translateService: TranslateService,
  ) {
    this.useStandardizedHeaderBar$ = combineLatest([
      this.featureFlagService.variation$(FeatureFlagCollection.standardizeHeaderBars, false).pipe(map(Boolean)),
      this.navService.isVerticalNavigationEnabled$,
    ]).pipe(
      map(
        ([standardizeHeaderBars, isVerticalNavEnabled]: [boolean, boolean]): boolean =>
          standardizeHeaderBars && isVerticalNavEnabled,
      ),
      shareReplay({ bufferSize: 1, refCount: true }),
    );

    this.wayfindingChipConfig$ = this.navService.getCurrentContextItem$().pipe(
      map((contextItem: INavigationContextItem | null): DsHeaderBarChipConfig[] => {
        if (!contextItem) {
          return [];
        }
        return [this.toWayfindingChipConfig(contextItem)];
      }),
      shareReplay({ bufferSize: 1, refCount: true }),
    );
  }

  public getWayfindingChipConfig$(): Observable<DsHeaderBarChipConfig[]> {
    return this.wayfindingChipConfig$;
  }

  private toWayfindingChipConfig(contextItem: INavigationContextItem): DsHeaderBarChipConfig {
    const translationKey: string = `navigation_context_chip_${getNavigationContextTranslationKey(contextItem)}`;
    return {
      text: this.translateService.instant(translationKey, [contextItem.label]),
      colorVariant: DsChipColorVariant.Neutral,
      size: DsChipSize.Small,
      showIcon: true,
      iconName: contextItem.icon,
    };
  }
}
